import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  Clock,
  Package,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Trash2,
  TriangleAlert,
} from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Badge, EmptyState, PageHeader, TableSkeleton, cx } from "../components/ui";
import { formatMoney } from "../lib/format";

/**
 * The catalogue: what this business sells, at what price, for how long.
 *
 * The header carries the numbers that decide whether the catalogue is healthy --
 * average margin, and how many products have no price at all -- because those are
 * the two things an owner gets wrong and does not notice until month end.
 */
export function CatalogPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const search = params.get("search") ?? "";
  const category = params.get("category") ?? "";
  const includeInactive = params.get("include_inactive") === "true";

  const [draft, setDraft] = useState(search);

  const summary = useQuery({
    queryKey: ["catalog-summary"],
    queryFn: ({ signal }) => api.catalogSummary(signal),
  });

  const query = useQuery({
    queryKey: ["products", search, category, includeInactive],
    queryFn: ({ signal }) =>
      api.products(
        {
          search: search || undefined,
          category: category || undefined,
          include_inactive: includeInactive ? "true" : undefined,
          page_size: 100,
        },
        signal,
      ),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteProduct(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["products"] });
      void queryClient.invalidateQueries({ queryKey: ["catalog-summary"] });
    },
  });

  function update(next: Record<string, string>) {
    setParams(
      (prev) => {
        const merged = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(next)) {
          if (value) merged.set(key, value);
          else merged.delete(key);
        }
        return merged;
      },
      { replace: true },
    );
  }

  const products = query.data?.results ?? [];
  const canWrite = (user?.permissions ?? []).includes("settings.write");
  const avgMargin = summary.data?.average_margin_percent ?? null;

  return (
    <>
      <PageHeader
        title="Catalog"
        subtitle={
          summary.data
            ? `${summary.data.total} products · ${summary.data.bookable} bookable`
            : "What you sell, and what it costs you to deliver"
        }
        actions={
          canWrite ? (
            <Link
              to="/catalog/products/new"
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
            >
              <Plus size={16} /> Add product
            </Link>
          ) : null
        }
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <Tile
          label="Products"
          value={summary.data ? String(summary.data.total) : null}
          hint={summary.data ? `${summary.data.categories.length} categories` : ""}
        />
        <Tile
          label="Average margin"
          value={avgMargin !== null ? `${avgMargin}%` : null}
          hint={avgMargin === null && summary.data ? "No priced products yet" : "Price minus cost"}
          tone={avgMargin !== null && Number(avgMargin) < 30 ? "warn" : "default"}
        />
        <Tile
          label="Unpriced"
          value={summary.data ? String(summary.data.unpriced) : null}
          hint="Free or consultation items"
          tone={summary.data && summary.data.unpriced > 0 ? "warn" : "default"}
        />
      </div>

      {summary.data && summary.data.unpriced > 0 ? (
        <div className="mb-4 flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
          <TriangleAlert size={16} className="mt-0.5 shrink-0" />
          <p>
            {summary.data.unpriced} product
            {summary.data.unpriced === 1 ? " has" : "s have"} no price. That is fine for a
            consultation that is deducted later, but check it is deliberate.
          </p>
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="search"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              update({ search: event.target.value });
            }}
            placeholder="Search products"
            aria-label="Search products"
            className="w-full rounded-md border-0 bg-white py-2 pl-9 pr-3 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
        </div>

        {(summary.data?.categories ?? []).map((cat) => (
          <button
            key={cat}
            onClick={() => update({ category: category === cat ? "" : cat })}
            className={cx(
              "rounded-full px-3 py-1.5 text-xs font-medium transition",
              category === cat
                ? "bg-slate-900 text-white"
                : "bg-white text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50",
            )}
          >
            {cat}
          </button>
        ))}

        <label className="ml-auto flex cursor-pointer items-center gap-1.5 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => update({ include_inactive: e.target.checked ? "true" : "" })}
            className="size-3.5 rounded border-slate-300"
          />
          Show retired
        </label>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        {query.isPending ? (
          <TableSkeleton rows={8} />
        ) : query.isError ? (
          <ErrorState error={query.error} />
        ) : products.length === 0 ? (
          <EmptyState
            icon={<Package size={28} />}
            title={
              search || category ? "Nothing matches that" : "Your catalog is empty"
            }
            body={
              search || category
                ? "Try a different search or category."
                : "Add the things you sell, with a price and how long each takes. Your industry starter pack usually gives you a head start."
            }
            action={
              canWrite ? (
                <Link
                  to="/catalog/products/new"
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white"
                >
                  <Plus size={15} /> Add product
                </Link>
              ) : null
            }
          />
        ) : (
          <>
            <table className="hidden w-full sm:table">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5">Product</th>
                  <th className="px-4 py-2.5">Category</th>
                  <th className="px-4 py-2.5 text-right">Price</th>
                  <th className="px-4 py-2.5 text-right">Cost</th>
                  <th className="px-4 py-2.5 text-right">Margin</th>
                  <th className="px-4 py-2.5 text-right">Duration</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {products.map((product) => (
                  <tr
                    key={product.id}
                    className={cx("hover:bg-slate-50", !product.is_active && "opacity-55")}
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/catalog/products/${product.id}/edit`}
                        className="block max-w-xs"
                      >
                        <span className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-medium text-slate-900">
                            {product.name}
                          </span>
                          {product.from_preset ? (
                            <Sparkles
                              size={12}
                              className="shrink-0 text-slate-400"
                              aria-label="From your industry starter pack"
                            />
                          ) : null}
                        </span>
                        {product.description ? (
                          <span className="block truncate text-xs text-slate-500">
                            {product.description}
                          </span>
                        ) : null}
                      </Link>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {!product.is_active ? <Badge>Retired</Badge> : null}
                        {!product.is_bookable ? <Badge>Not bookable</Badge> : null}
                        {product.requires_intake_form ? <Badge tone="sky">Intake form</Badge> : null}
                        {product.requires_consent ? <Badge tone="amber">Consent</Badge> : null}
                        {product.requires_resource_type ? (
                          <Badge>{product.requires_resource_type.replace(/_/g, " ")}</Badge>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {product.category || "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-slate-900">
                      {formatMoney(product.price)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-slate-500">
                      {formatMoney(product.cost_price)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <MarginCell margin={product.margin_percent} />
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-slate-600">
                      <span className="inline-flex items-center gap-1">
                        <Clock size={12} className="text-slate-300" />
                        {formatMinutes(product.effective_duration_minutes)}
                      </span>
                      {product.buffer_minutes ? (
                        <span className="block text-xs text-slate-400">
                          +{product.buffer_minutes}m buffer
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canWrite ? (
                        <span className="inline-flex gap-1">
                          <Link
                            to={`/catalog/products/${product.id}/edit`}
                            title="Edit"
                            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                          >
                            <Pencil size={14} />
                          </Link>
                          <button
                            onClick={() => remove.mutate(product.id)}
                            title="Remove"
                            aria-label={`Remove ${product.name}`}
                            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
                          >
                            <Trash2 size={14} />
                          </button>
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <ul className="divide-y divide-slate-100 sm:hidden">
              {products.map((product) => (
                <li key={product.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-900">
                        {product.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {product.category || "Uncategorised"} ·{" "}
                        {formatMinutes(product.effective_duration_minutes)}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-medium tabular-nums">
                        {formatMoney(product.price)}
                      </p>
                      <MarginCell margin={product.margin_percent} />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </>
  );
}

function Tile({
  label,
  value,
  hint,
  tone = "default",
}: {
  label: string;
  value: string | null;
  hint: string;
  tone?: "default" | "warn";
}) {
  return (
    <div
      className={cx(
        "rounded-lg border p-4 shadow-xs",
        tone === "warn" ? "border-amber-200 bg-amber-50/50" : "border-slate-200 bg-white",
      )}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
        {value ?? "—"}
      </p>
      <p className="mt-0.5 text-xs text-slate-500">{hint}</p>
    </div>
  );
}

/** A thin margin is worth flagging, but a free item is not a warning. */
function MarginCell({ margin }: { margin: string | null }) {
  if (margin === null) {
    return <span className="text-xs text-slate-300">—</span>;
  }
  const value = Number(margin);
  const tone =
    value < 20
      ? "text-rose-600"
      : value < 40
        ? "text-amber-600"
        : "text-emerald-600";
  return <span className={cx("text-sm font-medium tabular-nums", tone)}>{margin}%</span>;
}

function formatMinutes(minutes: number | null | undefined): string {
  if (!minutes) return "—";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

function ErrorState({ error }: { error: unknown }) {
  if (!(error instanceof ApiError)) {
    return <EmptyState title="Something went wrong" body="Please try again." />;
  }
  return <EmptyState title="Could not load the catalog" body={error.message} />;
}